import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";

import { Button } from "newui/components/Button";
import SearchBarJs from "components/RFQ/SearchBar/SearchBar";
import FilterButton from "components/FuncButton/FilterButton";
import FilterSupplier from "components/RFQ/FilterSupplier/FilterSupplier";
import BadgeComp from "components/RFQ/BadgeComp/BadgeComp";
import { Icon } from "assets/Icon";
import { showToast } from "utils/helpers";
import { assignSupplierListToProject } from "actions/RFQ/projectsAction";
import { Pagination } from "newui/components/Pagination";
import { useSupplierListsPaged } from "newui/features/Rfx/SupplierList";
import type {
	SupplierListDateFilters,
	SupplierListTab,
} from "newui/features/Rfx/SupplierList";

import { SupplierListDrawer } from "../SupplierListDrawer";

// SearchBar is a legacy JS component whose PropTypes infer as all-required from
// TS; adapt it at this JS→TS boundary (behaviour unchanged).
type LooseComponent = (
	props: Record<string, unknown> & { children?: React.ReactNode }
) => React.ReactElement | null;
const SearchBar = SearchBarJs as unknown as LooseComponent;

interface SupplierListRow {
	id: number;
	[key: string]: unknown;
}

interface AddSupplierListDrawerProps {
	[key: string]: unknown;
	defaultLists: SupplierListRow[];
	isOpenContainer: boolean;
	projectData?: unknown;
	projectId: number | string;
	onCloseContainer: () => void;
	setSupplierLists: (rows: SupplierListRow[]) => void;
	setSupplierListsWithRFQs: (rfqs: unknown) => void;
}

const DRAWER_PAGE_SIZE = 5;

export const AddSupplierListDrawer = ({
	projectData,
	onCloseContainer,
	isOpenContainer,
	projectId,
	defaultLists,
	setSupplierLists,
	setSupplierListsWithRFQs,
	...props
}: AddSupplierListDrawerProps) => {
	const [currentTab, setCurrentTab] = useState<SupplierListTab>("myDepartment");
	const [filters, setFilters] = useState<SupplierListDateFilters | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DRAWER_PAGE_SIZE);
	// The drawer is additive: attached lists are excluded from results, so we only
	// track NEWLY selected lists here (never the already-attached set). This keeps
	// the submitted payload to just the additions — no stale/attached ids.
	const [checkedRow, setCheckedRow] = useState<SupplierListRow[]>([]);
	const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});

	const { isOpen, onOpen, onClose } = useDisclosure();

	const isCurrent = (tab: SupplierListTab) => currentTab === tab;

	// Reset to the first page whenever the query dimensions change; the new query
	// key then triggers a single scoped fetch per tab click / search / filter.
	useEffect(() => {
		setPage(1);
	}, [currentTab, search, filters]);

	const query = useSupplierListsPaged({
		tab: currentTab,
		page,
		limit: pageSize,
		search,
		sort: "last_activity",
		filters,
		// Exclude lists already attached to this project so the drawer only offers
		// lists that can still be added.
		excludeProjectId: projectId,
	});

	const items = query.data?.data ?? [];
	const meta = query.data?.meta;
	const totalItems = meta?.total ?? 0;
	const perPage = meta?.per_page ?? pageSize;

	// Loading feedback on first load and while fetching over placeholder (previous)
	// data — e.g. tab switch — without flashing skeletons on background refetch.
	const isBusy = query.isLoading || (query.isFetching && query.isPlaceholderData);

	useEffect(() => {
		// Ignore placeholder (keepPreviousData) results so the badge doesn't flash
		// the previous tab's total against the new tab while it loads.
		if (!query.isPlaceholderData && typeof meta?.total === "number") {
			setBadgeCounts((prev) => ({ ...prev, [currentTab]: meta.total }));
		}
	}, [meta?.total, currentTab, query.isPlaceholderData]);

	const addSupplierHandler = () => {
		assignSupplierListToProject(projectId, {
			supplier_list_id:
				checkedRow.length > 0 ? checkedRow.map((item) => item.id) : null,
		})
			.then((res: { data?: { rfqs?: unknown }; message?: string }) => {
				// Additive: the project now holds its existing lists plus the new
				// selections. Merge for the parent's display; the API only received
				// the additions.
				setSupplierLists([...defaultLists, ...checkedRow]);
				setSupplierListsWithRFQs(res?.data?.rfqs);
				onCloseContainer();
				showToast(res?.message, "Success", true);
				setFilters(null);
				setSearch("");
				setCheckedRow([]);
			})
			.catch(() => {
				showToast("Error while adding supplier list", "Error");
			});
	};

	return (
		<div>
			<Drawer
				placement="right"
				isOpen={isOpenContainer}
				onClose={onCloseContainer}
				size="xl"
				blockScrollOnMount={false}
				{...props}
			>
				<DrawerOverlay onClick={onCloseContainer} />
				<DrawerContent>
					<DrawerHeader
						borderBottomWidth="1px"
						className="border-b py-6 text-black"
					>
						<div className="flex items-center justify-between">
							<p className="text-base font-semibold">Project Suppliers</p>
							<div className="flex items-center">
								<Button
									btntype="plain"
									type="button"
									className="cursor-pointer font-roboto text-sm font-medium"
									onClick={() => setCheckedRow([])}
								>
									Clear selection
								</Button>
								<Button
									btntype="plain-icon"
									type="button"
									onClick={onCloseContainer}
								>
									<Icon icon="close" />
								</Button>
							</div>
						</div>
					</DrawerHeader>
					<DrawerBody
						className="p-0"
						id="scrollable-drawer"
					>
						<div>
							<Flex
								style={{ justifyContent: "space-between" }}
								className="p-5"
							>
								<Flex style={{ gap: "8px" }}>
									<Button
										btntype="tab"
										active={isCurrent("myDepartment")}
										onClick={() => setCurrentTab("myDepartment")}
									>
										My Suppliers
										<BadgeComp
											title="myDepartment"
											active={isCurrent("myDepartment")}
											total={badgeCounts.myDepartment ?? 0}
										/>
									</Button>
									<Button
										btntype="tab"
										active={isCurrent("companyApprovedList")}
										onClick={() => setCurrentTab("companyApprovedList")}
									>
										Panel List
										<BadgeComp
											title="companyApprovedList"
											active={isCurrent("companyApprovedList")}
											total={badgeCounts.companyApprovedList ?? 0}
										/>
									</Button>
								</Flex>
								<Flex style={{ gap: "8px" }}>
									<SearchBar setState={setSearch} />
									<FilterButton
										onClick={onOpen}
										filters={filters}
									/>
									<FilterSupplier
										isOpen={isOpen}
										onClose={onClose}
										filters={filters}
										setFilters={setFilters}
									/>
								</Flex>
							</Flex>
						</div>
						<div>
							<Pagination
								currentPage={page}
								rowsPerPage={perPage}
								rowsPerPageOptions={[DRAWER_PAGE_SIZE, DRAWER_PAGE_SIZE * 2, DRAWER_PAGE_SIZE * 3]}
								totalItems={totalItems}
								onPageChange={(newPage) => setPage(newPage)}
								onRowsPerPageChange={(newSize) => {
									setPageSize(newSize);
									setPage(1);
								}}
							/>
							<SupplierListDrawer
								projectData={projectData}
								currPage={page}
								data={items}
								isLoading={isBusy}
								checkedRow={checkedRow}
								setCheckedRow={setCheckedRow}
							/>
						</div>
						<div className="sticky bottom-0 z-10 h-auto w-full bg-white px-4 py-6 shadow-inner">
							<Button
								className="!w-full"
								disabled={checkedRow.length === 0}
								onClick={addSupplierHandler}
								data-testid="submit-supplier-list"
							>
								Apply
							</Button>
						</div>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</div>
	);
};
